import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { Select, Menu, TextInput, Text } from '@mantine/core';
import { useOs } from '@mantine/hooks';
import helpers from '@starflow/app/helpers';
import { ColorPicker } from '@shakkerai/pro-atoms';
import { pt2px, px2pt, fontsList } from './utils';
// import styles from './tool.module.scss';

const TextEditToolbarInner = (props) => {
  const os = useOs();
  const { element } = props;
  const { fontSize, color, text, fontFamily, fontStyle } = element.data;
  const [fontFamilyList, setFontFamilyList] = useState([]);
  const textInputRef = useRef();

  const fontList = useMemo(() => {
    return fontsList[os] || ['Arial'];
  }, [os]);

  useEffect(() => {
    setFontFamilyList(fontList);
  }, [os]);

  const handleFontChange = (name, value) => {
    if (!value) {
      return;
    }
    handleUpdate({ name, value });
  };

  const handleUpdate = useCallback(
    (param) => {
      let { name, value } = param;

      if (name === 'fontSize') {
        value = pt2px(value / element.scaleX);
      }

      helpers.canvas.updateElementData(element.id, {
        [name]: value,
      });

      helpers.canvas.updateSelectionFrame();
    },
    [element.id, element.scaleX],
  );

  useEffect(() => {
    const textNode = helpers.canvas.getCanvasStage().findOne(`#${element.id}-text`);
    const { width, height } = textNode.size();

    helpers.canvas.updateElement(element.id, { width, height });
    helpers.canvas.updateSelectionFrame();
  }, [element.id, text, fontSize, fontFamily, fontStyle]);

  useEffect(() => {
    if (textInputRef.current) {
      textInputRef.current.value = px2pt(element.scaleX * fontSize);
    }
  }, [element.scaleX]);

  const inputStyle = () => {
    return {
      input: {
        borderRadius: 2,
        border: 0,
        backgroundColor: '#F4F6F8',
        minHeight: '24px',
        height: '24px',
        lineHeight: '22px',
        fontSize: '12px',
      },
    };
  };

  const renderFontFamilySelectOption = ({ option, checked }) => {
    return (
      <Text lineClamp={1} className="text-[12px]" style={{ fontFamily: option.value }}>
        {option.label}
      </Text>
    );
  };

  const renderFontWeightSelectOption = ({ option }) => {
    return (
      <Text lineClamp={1} className="text-[12px]" style={{ fontWeight: option.value }}>
        {option.label}
      </Text>
    );
  };

  return (
    <div className="flex items-center pointer-events-auto bg-white">
      <div className="font-select font-family-select w-[160px] mr-[12px]">
        <Select
          label={null}
          styles={inputStyle}
          searchable
          defaultValue="PingFang SC"
          data={fontFamilyList}
          onChange={(value) => handleFontChange('fontFamily', value)}
          value={fontFamily}
          renderOption={renderFontFamilySelectOption}
        />
      </div>
      <div className="font-select font-style-select w-[120px] mr-[12px]">
        <Select
          label={null}
          styles={inputStyle}
          onChange={(value) => handleFontChange('fontStyle', value)}
          value={`${fontStyle}`}
          data={[
            // { value: '100', label: 'Thin' },
            // { value: '300', label: 'Light' },
            { value: '400', label: 'Regular' },
            // { value: '500', label: 'Medium' },
            { value: '700', label: 'Bold' },
          ]}
          renderOption={renderFontWeightSelectOption}
        />
      </div>
      <div className="font-input w-[80px] mr-[12px]">
        <Menu>
          <Menu.Target>
            <TextInput
              ref={textInputRef}
              styles={inputStyle}
              rightSection="pt"
              defaultValue={px2pt(parseInt(fontSize * element.scaleX, 10))}
              onBlur={(e) => {
                handleFontChange('fontSize', e.currentTarget.value);
              }}
              onKeyUp={(e) => {
                if (e.key === 'Enter') {
                  handleFontChange('fontSize', e.currentTarget.value);
                }
              }}
            />
          </Menu.Target>
          <Menu.Dropdown className="h-[280px] overflow-y-auto">
            {[6, 8, 10, 12, 14, 16, 18, 21, 24, 28, 32, 36, 42, 48, 56, 64, 72, 80, 88, 96, 104, 120, 144].map(
              (size) => {
                return (
                  <Menu.Item
                    className="w-[74px]"
                    key={size}
                    onClick={() => {
                      handleFontChange('fontSize', size);
                      textInputRef.current.value = size;
                    }}
                  >
                    {size}
                  </Menu.Item>
                );
              },
            )}
          </Menu.Dropdown>
        </Menu>
      </div>
      <div className="font-input font-color-input">
        <ColorPicker
          value={color}
          onChange={(color) => {
            handleFontChange('color', color);
          }}
        />
      </div>
    </div>
  );
};

export const TextEditToolbar = React.memo((props) => {
  if (!props.isExactlySelected) return null;

  return <TextEditToolbarInner {...props} key={props.element.id} />;
});
